import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Store,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Store {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

const StoreManagement = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [storeName, setStoreName] = useState("");
  const [storeDescription, setStoreDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);

  const { user, userRole } = useAuth();
  const { toast } = useToast();

  // Filter stores based on search term
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchStores = async () => {
    try {
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setStores(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal memuat data toko",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && userRole === 'superadmin') {
      fetchStores();
    }
  }, [user, userRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingStore) {
        // Update existing store
        const { error } = await supabase
          .from('stores')
          .update({
            name: storeName,
            description: storeDescription,
          })
          .eq('id', editingStore.id);

        if (error) throw error;

        toast({
          title: "Toko berhasil diperbarui",
          description: "Data toko telah diperbarui",
        });
      } else {
        // Create new store
        const { error } = await supabase
          .from('stores')
          .insert({
            name: storeName,
            description: storeDescription,
          });

        if (error) throw error;

        toast({
          title: "Toko berhasil ditambahkan",
          description: "Toko baru telah dibuat",
        });
      }

      // Reset form and close dialog
      setStoreName("");
      setStoreDescription("");
      setEditingStore(null);
      setShowStoreForm(false);

      // Refresh data
      fetchStores();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Gagal menyimpan data toko",
        variant: "destructive",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (store: Store) => {
    setEditingStore(store);
    setStoreName(store.name);
    setStoreDescription(store.description || "");
    setShowStoreForm(true);
  };

  const handleDelete = async (storeId: string) => {
    if (!confirm("Apakah Anda yakin ingin menghapus toko ini?")) return;
    try {
      console.log("Menghapus store id:", storeId);
      const { error } = await supabase
        .from('stores')
        .delete()
        .eq('id', storeId);
      if (error) throw error;
      toast({
        title: "Toko dihapus",
        description: "Toko berhasil dihapus dari sistem",
      });
      await fetchStores();
      console.log("fetchStores selesai setelah delete");
    } catch (error: any) {
      console.error("Error delete:", error);
      toast({
        title: "Gagal menghapus toko",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const openAddDialog = () => {
    setEditingStore(null);
    setStoreName("");
    setStoreDescription("");
    setShowStoreForm(true);
  };

  if (userRole !== 'superadmin') {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gradient-card border-none shadow-independence">
          <CardContent className="text-center p-8">
            <h2 className="text-xl font-bold mb-4">Akses Ditolak</h2>
            <p className="text-muted-foreground mb-4">
              Anda tidak memiliki izin untuk mengakses manajemen toko.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Memuat data toko...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1">
          <header className="h-12 flex items-center border-b">
            <SidebarTrigger className="ml-2" />
            <h1 className="ml-4 font-semibold">Manajemen Toko</h1>
          </header>
          <main className="flex-1">
            {/* Header */}
            <div className="border-b border-border bg-gradient-card">
              <div className="container mx-auto px-4 py-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Manajemen Toko</h1>
                    <p className="text-muted-foreground">
                      Kelola semua toko dalam sistem bazaar
                    </p>
                  </div>
                  <Button variant="default" onClick={openAddDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Toko
                  </Button>
                </div>
              </div>
            </div>

            <div className="container mx-auto px-4 py-8">
              {/* Search */}
              <Card className="mb-6 bg-gradient-card border-none shadow-soft">
                <CardContent className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Cari toko berdasarkan nama atau deskripsi..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Stores Grid */}
              <Card className="bg-gradient-card border-none shadow-soft">
                <CardHeader>
                  <CardTitle>Daftar Toko</CardTitle>
                  <CardDescription>
                    Total: {filteredStores.length} toko
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredStores.length === 0 ? (
                    <div className="text-center py-12">
                      <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {searchTerm ? "Tidak ada toko yang ditemukan" : "Belum ada toko"}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {searchTerm
                          ? "Coba gunakan kata kunci yang berbeda"
                          : "Mulai dengan menambahkan toko pertama Anda"
                        }
                      </p>
                      {!searchTerm && (
                        <Button variant="default" onClick={openAddDialog}>
                          <Plus className="w-4 h-4 mr-2" />
                          Tambah Toko
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredStores.map((store) => (
                        <Card key={store.id} className="overflow-hidden hover:shadow-soft transition-all">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <Store className="w-8 h-8 text-primary" />
                              <Badge variant="secondary">Aktif</Badge>
                            </div>
                            <h3 className="font-semibold text-foreground mb-2 text-lg">
                              {store.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                              {store.description || "Tidak ada deskripsi"}
                            </p>
                            <div className="text-xs text-muted-foreground mb-4">
                              Dibuat: {new Date(store.created_at).toLocaleDateString('id-ID')}
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleEdit(store)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(store.id)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Store Form Dialog */}
            <Dialog open={showStoreForm} onOpenChange={setShowStoreForm}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingStore ? "Edit Toko" : "Tambah Toko Baru"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nama Toko</label>
                    <Input
                      type="text"
                      value={storeName}
                      onChange={(e) => setStoreName(e.target.value)}
                      placeholder="Masukkan nama toko"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Deskripsi Toko</label>
                    <Textarea
                      value={storeDescription}
                      onChange={(e) => setStoreDescription(e.target.value)}
                      placeholder="Masukkan deskripsi toko"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowStoreForm(false)}
                    >
                      Batal
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1"
                      disabled={formLoading}
                    >
                      {formLoading ? "Menyimpan..." : (editingStore ? "Perbarui" : "Simpan")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default StoreManagement;